import { supabase } from "./supabase";
import {
  importDb,
  getActivityStores,
  deleteActivities,
  type ImportActivity,
  type ImportToken,
} from "./import-db";

export interface CommitResult {
  success: boolean;
  committedCount: number;
  errors: string[];
}

/**
 * Commit selected import activities to the main database.
 * This creates the necessary records in periods, store_sets, marketing_activities,
 * marketing_activities_store_sets, and stores_store_sets tables.
 *
 * Store activities are created automatically by a database trigger.
 */
export async function commitImportActivities(
  activityIds: number[],
): Promise<CommitResult> {
  const errors: string[] = [];
  let committedCount = 0;

  // Get all activities to commit
  const activities = await importDb.activities
    .where("id")
    .anyOf(activityIds)
    .toArray();

  // Validate: activities with problems cannot be committed
  const activitiesWithProblems = activities.filter((a) => a.problem !== null);
  if (activitiesWithProblems.length > 0) {
    return {
      success: false,
      committedCount: 0,
      errors: [
        `Cannot commit ${activitiesWithProblems.length} activities with problems. Please resolve issues or deselect them.`,
      ],
    };
  }

  // Get all tokens for reference mapping
  const tokens = await importDb.tokens.toArray();
  const tokenMap = new Map<number, ImportToken>();
  tokens.forEach((t) => {
    if (t.id !== undefined) {
      tokenMap.set(t.id, t);
    }
  });

  // Process each activity
  for (const activity of activities) {
    try {
      await commitSingleActivity(activity, tokenMap);
      committedCount++;
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : "Unknown error occurred";
      errors.push(`Activity ${activity.id}: ${errorMsg}`);
    }
  }

  // If all activities were committed successfully, delete them from IndexedDB
  if (errors.length === 0 && committedCount > 0) {
    await deleteActivities(activityIds);
  }

  return {
    success: errors.length === 0,
    committedCount,
    errors,
  };
}

/**
 * Commit a single activity to the database
 */
async function commitSingleActivity(
  activity: ImportActivity,
  tokenMap: Map<number, ImportToken>,
): Promise<void> {
  const periodToken = tokenMap.get(activity.periodId);
  const productToken = tokenMap.get(activity.productId);

  if (!periodToken) {
    throw new Error(`Period token not found for ID ${activity.periodId}`);
  }
  if (!productToken) {
    throw new Error(`Product token not found for ID ${activity.productId}`);
  }

  // Get activity stores
  const activityStores = await getActivityStores(activity.id!);
  const storeTokenIds = activityStores.map((as) => as.storeId);
  const storeTokens = storeTokenIds
    .map((id) => tokenMap.get(id))
    .filter(Boolean) as ImportToken[];

  // 1. Get or create period in Supabase
  const periodId = await getOrCreatePeriod(periodToken);

  // 2. Get product ID from Supabase (products are referenced by code)
  const productId = await getProductIdByCode(productToken.reference);
  if (!productId) {
    throw new Error(`Product not found for code: ${productToken.reference}`);
  }

  // 2b. Resolve requirement_template_id from product → brand → supplier
  const requirementTemplateId =
    await getRequirementTemplateIdByProduct(productId);

  // 3. Get store IDs from Supabase (stores are referenced by reference_number)
  const storeIds = await getStoreIdsByReferenceNumbers(
    storeTokens.map((t) => t.reference),
  );

  // 4. Create store_set
  const timestamp = new Date().toISOString();
  const storeSetId = await createStoreSet(`Automated ${timestamp}`);

  // 5. Link stores to store_set
  await linkStoresToStoreSet(storeIds, storeSetId);

  // 6. Create marketing_activity
  const marketingActivityId = await createMarketingActivity({
    periodId,
    productId,
    typeId: activity.typeId,
    budget: activity.budget,
    requirementTemplateId,
  });

  // 7. Link marketing_activity to store_set
  // (trigger creates store_activities automatically)
  await linkMarketingActivityToStoreSet(marketingActivityId, storeSetId);

  // 8. Set budget on store_activities in a single upsert
  // Build reference_number → budget map from import data
  const budgetByReference = new Map<string, number>();
  for (const as of activityStores) {
    if (as.budget != null) {
      const token = tokenMap.get(as.storeId);
      if (token) {
        budgetByReference.set(token.reference, as.budget);
      }
    }
  }

  if (budgetByReference.size > 0) {
    // storeIds were already resolved in step 3; build a reference → real ID map
    const storeIdByReference = new Map<string, number>();
    for (let i = 0; i < storeTokens.length; i++) {
      storeIdByReference.set(storeTokens[i].reference, storeIds[i]);
    }

    const upsertRows = [...budgetByReference.entries()]
      .map(([ref, budget]) => {
        const storeId = storeIdByReference.get(ref);
        if (storeId == null) return null;
        return {
          marketing_activity_id: marketingActivityId,
          store_id: storeId,
          budget,
        };
      })
      .filter((r) => r != null);

    if (upsertRows.length > 0) {
      const { error: upsertError } = await supabase
        .from("store_activities")
        .upsert(upsertRows, {
          onConflict: "marketing_activity_id,store_id",
          ignoreDuplicates: false,
        });

      if (upsertError) {
        throw new Error(
          `Error setting budgets on store_activities: ${upsertError.message}`,
        );
      }
    }
  }
}

/**
 * Get or create a period in Supabase
 */
async function getOrCreatePeriod(periodToken: ImportToken): Promise<number> {
  const periodName = periodToken.reference;
  const meta = periodToken.meta as {
    start_date?: string;
    end_date?: string;
  } | null;

  // Check if period already exists by name
  const { data: existingPeriod, error: selectError } = await supabase
    .from("periods")
    .select("id")
    .eq("name", periodName)
    .single();

  if (existingPeriod) {
    return existingPeriod.id;
  }

  // Period doesn't exist, create it
  if (selectError && selectError.code !== "PGRST116") {
    throw new Error(`Error checking period: ${selectError.message}`);
  }

  // Dates are required
  if (!meta?.start_date || !meta?.end_date) {
    throw new Error(`Period "${periodName}" is missing start_date or end_date`);
  }

  const startDate = meta.start_date;
  const endDate = meta.end_date;

  const { data: newPeriod, error: insertError } = await supabase
    .from("periods")
    .insert({
      name: periodName,
      start_date: startDate,
      end_date: endDate,
    })
    .select("id")
    .single();

  if (insertError) {
    throw new Error(`Error creating period: ${insertError.message}`);
  }

  return newPeriod.id;
}

/**
 * Get product ID by code from Supabase
 */
async function getProductIdByCode(code: string): Promise<number | null> {
  const { data, error } = await supabase
    .from("products")
    .select("id")
    .eq("code", code)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // Not found
    throw new Error(`Error fetching product: ${error.message}`);
  }

  return data.id;
}

/**
 * Resolve the requirement_template_id for a product via the chain:
 * product → brand → supplier → requirement_template_id
 */
async function getRequirementTemplateIdByProduct(
  productId: number,
): Promise<number> {
  const { data, error } = await supabase
    .from("products")
    .select("brands(suppliers(requirement_template_id))")
    .eq("id", productId)
    .single();

  if (error) {
    throw new Error(
      `Error resolving requirement template for product ${productId}: ${error.message}`,
    );
  }

  const templateId = (
    data?.brands as unknown as {
      suppliers: { requirement_template_id: number } | null;
    } | null
  )?.suppliers?.requirement_template_id;

  if (!templateId) {
    throw new Error(
      `Could not resolve requirement template for product ${productId}. Check that the product's brand and supplier have a template assigned.`,
    );
  }

  return templateId;
}

/**
 * Get store IDs by reference numbers from Supabase
 */
async function getStoreIdsByReferenceNumbers(
  referenceNumbers: string[],
): Promise<number[]> {
  if (referenceNumbers.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from("stores")
    .select("id")
    .in("reference_number", referenceNumbers);

  if (error) {
    throw new Error(`Error fetching stores: ${error.message}`);
  }

  return data.map((s) => s.id);
}

/**
 * Create a store_set in Supabase
 */
async function createStoreSet(name: string): Promise<number> {
  const { data, error } = await supabase
    .from("store_sets")
    .insert({ name })
    .select("id")
    .single();

  if (error) {
    throw new Error(`Error creating store_set: ${error.message}`);
  }

  return data.id;
}

/**
 * Link stores to a store_set
 */
async function linkStoresToStoreSet(
  storeIds: number[],
  storeSetId: number,
): Promise<void> {
  if (storeIds.length === 0) return;

  const inserts = storeIds.map((storeId) => ({
    store_id: storeId,
    store_set_id: storeSetId,
  }));

  const { error } = await supabase.from("stores_store_sets").insert(inserts);

  if (error) {
    throw new Error(`Error linking stores to store_set: ${error.message}`);
  }
}

/**
 * Create a marketing_activity in Supabase
 */
async function createMarketingActivity(params: {
  periodId: number;
  productId: number;
  typeId: number;
  budget: number | null;
  requirementTemplateId: number;
}): Promise<number> {
  const { data, error } = await supabase
    .from("marketing_activities")
    .insert({
      period_id: params.periodId,
      product_id: params.productId,
      type_id: params.typeId,
      budget: params.budget,
      is_deleted: false,
      requirement_template_id: params.requirementTemplateId,
    })
    .select("id")
    .single();

  if (error) {
    throw new Error(`Error creating marketing_activity: ${error.message}`);
  }

  return data.id;
}

/**
 * Link a marketing_activity to a store_set
 */
async function linkMarketingActivityToStoreSet(
  marketingActivityId: number,
  storeSetId: number,
): Promise<void> {
  const { error } = await supabase
    .from("marketing_activities_store_sets")
    .insert({
      marketing_activity_id: marketingActivityId,
      store_set_id: storeSetId,
    });

  if (error) {
    throw new Error(
      `Error linking marketing_activity to store_set: ${error.message}`,
    );
  }
}
